'use client';

import { Button } from '@petsmart-ui/sparky';
import { useCurrentRefinements, useInstantSearch, useRefinementList } from 'react-instantsearch';

import { FILTERS, isEligibleValue } from '@libs/algolia';
import { useStore } from '@libs/common-next';
import { isStoreBopisEnabled } from '@libs/core';

import FindStoreNearMe from '@/components/FindStoreNearMe';

import {
    OPTEX_DELIVERY_OPTIONS,
    OPTEX_SHIP_TO_ME_ATTRIBUTE,
    type OptexDeliveryMethod,
    type OptexDeliveryOption,
} from './optexDeliveryFilterConstants';
import { useOptexSelectedDeliveryMethod } from './useOptexSelectedDeliveryMethod';

type OptexDeliveryOptionRadioProps = Readonly<{
    option: OptexDeliveryOption;
    isSelected: boolean;
    onSelect: (value: OptexDeliveryMethod) => void;
}>;

type OptexDeliveryFilterContentProps = Readonly<{
    pageId: string;
    openStoreModal: () => void;
    clearShop: () => void;
    handleIsStoreFilterApplied: (isApplied: boolean) => void;
    onViewResults?: () => void;
    showActions?: boolean;
}>;

function OptexDeliveryOptionRadio({ option, isSelected, onSelect }: OptexDeliveryOptionRadioProps) {
    return (
        <button type="button" className="optex-delivery-filter__option" onClick={() => onSelect(option.value)}>
            <div className="sparky-c-radio">
                <input className="sparky-c-radio__input" type="radio" checked={isSelected} readOnly />
                <span className="sparky-c-radio__custom-radio" />
            </div>

            <span className="sparky-c-checkbox-field__item-label optex-delivery-filter__label">{option.label}</span>
        </button>
    );
}

type OptexPickupStoreContentProps = Readonly<{
    pageId: string;
    openStoreModal: () => void;
    clearShop: () => void;
    onPickupApplied: (isApplied: boolean) => void;
}>;

function OptexPickupStoreContent({ pageId, openStoreModal, clearShop, onPickupApplied }: OptexPickupStoreContentProps) {
    const { selectedStore } = useStore();
    const { data: store } = selectedStore;

    return (
        <div className="optex-delivery-filter__store">
            {store && (
                <div className="optex-delivery-filter__address sparky-c-checkbox-field__item-label">
                    <div>{store.Name}</div>
                    <div>{store.StreetLine1}</div>
                    <div>
                        {store.City}, {store.StateProvinceAbbreviation} {store.ZipPostalCode}
                    </div>
                </div>
            )}

            <FindStoreNearMe
                pageId={pageId}
                openStoreModal={openStoreModal}
                clearShop={clearShop}
                showActions={false}
                handleIsStoreFilterApplied={onPickupApplied}
            />
        </div>
    );
}

export default function OptexDeliveryFilterContent({
    pageId,
    openStoreModal,
    clearShop,
    handleIsStoreFilterApplied,
    onViewResults,
    showActions = true,
}: OptexDeliveryFilterContentProps) {
    const { selectedStore } = useStore();
    const { data: store } = selectedStore;
    const storeHasBopis = isStoreBopisEnabled(store);

    const { setIndexUiState } = useInstantSearch();

    const sddRefinements = useRefinementList({
        attribute: FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE,
    });

    const optexShipToMeRefinements = useRefinementList({
        attribute: OPTEX_SHIP_TO_ME_ATTRIBUTE,
    });

    const selectedMethod = useOptexSelectedDeliveryMethod();
    const hasSelection = Boolean(selectedMethod);

    const sddEligibleFacet = sddRefinements.items.find(item => isEligibleValue(item.value));
    const hasSddEligibleProducts = Boolean(sddEligibleFacet?.count);

    const sthEligibleFacet = optexShipToMeRefinements.items.find(item => item.value === 'false');
    const hasSthEligibleProducts = Boolean(sthEligibleFacet?.count);

    const deliveryOptions = OPTEX_DELIVERY_OPTIONS.filter(option => {
        if (option.value === 'sdd') {
            return hasSddEligibleProducts;
        }

        if (option.value === 'sth') {
            return hasSthEligibleProducts;
        }

        return true;
    });

    const { items: deliveryRefinements } = useCurrentRefinements({
        includedAttributes: [OPTEX_SHIP_TO_ME_ATTRIBUTE],
    });

    const hasOptexShipToMeRefinement = deliveryRefinements.some(item => item.attribute === OPTEX_SHIP_TO_ME_ATTRIBUTE);

    const applyOptexStoreFilter = (storeNumber: string) => {
        setIndexUiState(prev => ({
            ...prev,
            refinementList: {
                ...prev.refinementList,
                [FILTERS.SHOP_BY_STORE.ATTRIBUTE]: [storeNumber],
                [FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE]: [FILTERS.BOPIS_ELIGIBLE.DEFAULT_VALUE],
            },
        }));
    };

    const clearOptexSddRefinement = () => {
        const sddItem = sddRefinements.items.find(item => item.value === 'true');

        if (sddItem?.isRefined) {
            sddRefinements.refine(sddItem.value);
        }
    };

    const applyOptexSddRefinement = () => {
        const sddItem = sddRefinements.items.find(item => item.value === 'true');

        if (sddItem && !sddItem.isRefined) {
            sddRefinements.refine(sddItem.value);
        }
    };

    const applyOptexShipToMeRefinement = () => {
        if (!hasOptexShipToMeRefinement) {
            optexShipToMeRefinements.refine('false');
        }
    };

    const clearOptexShipToMeRefinement = () => {
        if (hasOptexShipToMeRefinement) {
            optexShipToMeRefinements.refine('false');
        }
    };

    const handleOptexSelectDeliveryMethod = (value: OptexDeliveryMethod) => {
        if (value === 'pickup') {
            if (store?.StoreNumber && storeHasBopis) {
                clearOptexSddRefinement();
                clearOptexShipToMeRefinement();
                applyOptexStoreFilter(store.StoreNumber.toString());
                handleIsStoreFilterApplied(true);
                return;
            }

            openStoreModal();
            return;
        }

        if (value === 'sdd') {
            clearShop();
            clearOptexShipToMeRefinement();
            applyOptexSddRefinement();
            handleIsStoreFilterApplied(false);
        }

        if (value === 'sth') {
            clearShop();
            clearOptexSddRefinement();
            applyOptexShipToMeRefinement();
            handleIsStoreFilterApplied(false);
        }
    };

    const handleOptexPickupApplied = (isApplied: boolean) => {
        handleIsStoreFilterApplied(isApplied);
    };

    const handleOptexClear = () => {
        clearShop();
        clearOptexSddRefinement();
        clearOptexShipToMeRefinement();
        handleIsStoreFilterApplied(false);
    };

    return (
        <div className="optex-delivery-filter">
            {hasSelection && (
                <Button
                    onClick={handleOptexClear}
                    text={FILTERS.CLEAR.TITLE}
                    variant="link"
                    className="drawer-filter__button--clear"
                    data-testid="drawer-filter-optex-delivery-clear-button"
                />
            )}
            <div className="optex-delivery-filter__options">
                {deliveryOptions.map(option => (
                    <div key={option.value} className="optex-delivery-filter__option-group">
                        <OptexDeliveryOptionRadio
                            option={option}
                            isSelected={selectedMethod === option.value}
                            onSelect={handleOptexSelectDeliveryMethod}
                        />

                        {option.value === 'pickup' && (
                            <OptexPickupStoreContent
                                pageId={pageId}
                                openStoreModal={openStoreModal}
                                clearShop={clearShop}
                                onPickupApplied={handleOptexPickupApplied}
                            />
                        )}
                    </div>
                ))}
            </div>
            {showActions && (
                <div className="custom-filter-refinement-list__actions">
                    <Button
                        variant="link"
                        size="sm"
                        text="Clear"
                        disabled={!hasSelection}
                        onClick={handleOptexClear}
                        className="custom-filter-refinement-list__actions--reset sparky-u-padding-sm sparky-u-margin-bottom-sm"
                    />

                    <Button
                        variant="primary"
                        text="View results"
                        onClick={onViewResults}
                        className="custom-filter-refinement-list__actions--view"
                    />
                </div>
            )}
        </div>
    );
}
